















import { Component, Mixins, Prop, Inject } from "vue-property-decorator";
import HasValidation from "@/mixins/HasValidation";
import {
  ITemplate,
  ITemplateCategory,
  ITemplateChannel
} from "@/models/templates";

import {
  ITemplateProvider,
  ITemplateProviderMethods
} from "@/models/providers/templates";
import { CreateElement, VNode } from "vue";
import IsActiveField from "@/components/app/global/templates/templateFields/isActiveField.vue";
import SendAttachmentField from "@/components/app/global/templates/templateFields/sendAttachmentField.vue";
import NameField from "@/components/app/global/templates/templateFields/nameField.vue";
import TemplateContent from "@/components/app/global/templates/contentForm.vue";
import ChannelsField from "@/components/app/global/templates/templateFields/channelsField.vue";
import HooksField from "@/components/app/global/templates/templateFields/hooksField.vue";
import RecipientsField from "@/components/app/global/templates/templateFields/recipientsField.vue";
import { INotificationHook } from "@/models/notifications";
import { NotificationChannelCodes } from "@/data/enums/notifications";
import CategorySelectField from "@/components/app/global/templates/templateFields/categorySelectField.vue";
import ActionContentsTable from "@/components/app/admin/automation/actionContentsTable.vue";
import { DataModules } from "@/store/modules/data";

type NotificationTemplateForm = Partial<
  Pick<
    ITemplate,
    | "body"
    | "brand_id"
    | "channel_id"
    | "enabled"
    | "name"
    | "recipient_type_id"
    | "subject"
    | "type"
    | "send_attachment"
  > & {
    hook_ids: INotificationHook["id"][];
    category_code: ITemplateCategory["code"];
  }
>;

@Component
export default class NotificationTemplateAddEditForm extends Mixins(
  HasValidation
) {
  // Inject
  @Inject({ from: "templateProvider" })
  public templateProvider!: () => ITemplateProvider;

  @Inject({ from: "templateProviderMethods" })
  public $templateMethods!: ITemplateProviderMethods;

  // Props

  @Prop()
  public preselectedHookId!: INotificationHook["id"];

  @Prop()
  public preselectedCategoryCode!: ITemplateCategory["code"];

  @Prop({ default: null })
  public preselectedChannelCode!: NotificationChannelCodes;

  @Prop({ default: true })
  public showActiveToggle!: boolean;

  @Prop({ default: true })
  public showChannelSelector!: boolean;

  @Prop({ default: true })
  public showCategorySelector!: boolean;

  @Prop({ default: true })
  public showHookSelector!: boolean;

  @Prop({ default: true })
  public showRecipientSelector!: boolean;

  @Prop({ default: true })
  public showSubmitButton!: boolean;

  // Data

  public form: NotificationTemplateForm = {};

  // Life-cycle methods

  public created() {
    this.revertToInitialState();
  }

  // Computed

  public get components() {
    return {
      render: (h: CreateElement) => {
        return h("div", {}, [
          this.renderIsActive(h),
          this.renderName(h),
          this.renderChannels(h),
          this.renderCategories(h),
          this.renderHooks(h),
          this.renderRecipients(h),
          this.renderSendTestEmailContextualise(h),
          this.renderTemplateContentContextualise(h),
          this.renderTranslations(h),
          this.renderSendAttachment(h)
        ]);
      }
    };
  }

  public get isFormChanged(): boolean {
    return !this.$_.isEqual(this.form, this.initForm());
  }

  public get $templateData() {
    return this.templateProvider();
  }

  public get template() {
    return this.$templateData.template;
  }

  public get selectedCategory() {
    return this.$templateData.categories.find(
      i => i.code === this.form.category_code
    );
  }

  public get selectedHooks() {
    return this.$templateData.hooks?.filter(i =>
      this.form.hook_ids?.includes(i.id)
    );
  }

  public get selectedChannel() {
    return this.$templateData.channels.find(i => i.id === this.form.channel_id);
  }

  public get selectedRecipientType() {
    return this.$templateData.recipientTypes?.find(
      i => i.id === this.form.recipient_type_id
    );
  }

  public get availableCategoriesForContent() {
    /**
     * Always injecting brand category + selected category
     */
    return this.$_.uniqBy(
      this.$_.compact([
        this.$templateData.brandCategory,
        this.form.category_code ? this.selectedCategory : null
      ]),
      "id"
    );
  }

  public get availableRecipientTypes() {
    return this.selectedChannel?.recipient_types;
  }

  public get isDisabled() {
    return (
      !this.$templateData.isNew ||
      this.$templateData.isReadOnly ||
      this.$templateData.isProcessing
    );
  }

  // Methods

  public revertToInitialState() {
    this.form = this.initForm();

    this.setHooks();
    this.setChannel();
    this.setCategory();
    this.setClientRecipientType();
  }

  public setHooks() {
    if (!this.preselectedHookId || !this.$templateData.isNew) return;
    this.form.hook_ids = [this.preselectedHookId];
    const hook = this.$templateData.hooks?.find(
      i => i.id === this.preselectedHookId
    );
    this.form.category_code = hook?.category?.code;
  }

  public setChannel() {
    if (!this.preselectedChannelCode || !this.$templateData.isNew) return;

    const channelCode = this.preselectedChannelCode;
    const channel = this.$_.find(this.$templateData.channels, [
      "code",
      channelCode
    ]) as ITemplateChannel;

    this.form.channel_id = channel.id;
  }

  public setCategory() {
    if (!this.preselectedCategoryCode) return;
    let categoryCode = this.preselectedCategoryCode as string;
    if (categoryCode.includes(".")) {
      categoryCode = categoryCode.split(".")[0];
    }
    const category = this.$_.find(this.$templateData.categories, [
      "code",
      categoryCode
    ]) as ITemplateCategory;
    if (this.$_.isEmpty(category)) return;
    this.form.category_code = category.code;
  }

  public setClientRecipientType() {
    if (this.form.recipient_type_id) return;

    const clientRecipientType = this.$_.find(
      this.$templateData.recipientTypes,
      ["code", "client"]
    );

    if (!clientRecipientType) {
      return;
    }

    this.form.recipient_type_id = clientRecipientType.id;
  }

  public initForm(): NotificationTemplateForm {
    return {
      enabled: !this.$_.isNil(this.template?.enabled)
        ? this.template?.enabled
        : true,
      name: this.template?.name,
      brand_id: this.template?.brand_id,
      recipient_type_id: this.template?.recipient_type_id,
      category_code:
        this.template?.category?.code || this.preselectedCategoryCode,
      subject: this.template?.subject,
      body: this.template?.body,
      type: this.$templateData.templateType,
      channel_id: this.template?.channel_id,
      hook_ids: this.template?.mappings?.map(i => i.hook_id),
      send_attachment: !!this.template?.send_attachment
    };
  }

  public async validateBeforeSubmit() {
    try {
      this.$templateMethods.setIsProcessing(true);
      const isValid = await this.validate();
      if (!isValid) throw this.$t("_sentence.form_is_not_valid");

      const template = this.$templateData.isNew
        ? await this.$templateMethods.createTemplate(this.form)
        : await this.$templateMethods.updateTemplate(this.form);

      this.$emit("success", template);

      await this.$templateMethods.reloadData();

      this.form = this.initForm();

      this.$buefy.toast.open({
        message: this.$t("_sentence.successfully_saved") as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.$templateMethods.setIsProcessing(false);
    }
  }

  // Render functions

  public renderCategories(h: CreateElement) {
    if (!this.showCategorySelector) return null;
    return h("contextualise", {
      attrs: { "min-width": "28rem" },
      props: { label: this.$t("_.category") },
      scopedSlots: {
        desc: () =>
          h("span", this.$t("_sentence.templates.category_desc") as string),
        default: () =>
          h("div", [
            h(CategorySelectField, {
              props: {
                value: this.form.category_code,
                categories: this.$templateData.categories,
                isDisabled:
                  this.isDisabled ||
                  this.$templateData.isLoadingCategories ||
                  !!this.preselectedCategoryCode,
                isLoading:
                  this.$templateData.isLoading ||
                  this.$templateData.isLoadingCategories
              },
              on: { input: e => (this.form.category_code = e) }
            })
          ])
      }
    });
  }

  public renderContextualise(
    defaultSlot: VNode | null,
    { label, description }
  ) {
    const h = this.$createElement;

    return h("contextualise", {
      attrs: { "min-width": "28rem" },
      props: { label },
      scopedSlots: {
        desc: () => h("span", description as string),
        default: () => defaultSlot
      }
    });
  }

  public renderTemplateContentContextualise(h: CreateElement) {
    if (!this.selectedChannel) return null;
    return this.renderContextualise(this.renderTemplateContent(h), {
      label: this.$t("_.template"),
      description: this.$t("_sentence.template_email_desc")
    });
  }

  public renderSendTestEmailContextualise(h: CreateElement) {
    if (!this.$templateData.canSendTestEmail) return null;
    return this.renderContextualise(this.renderSendTestEmail(h), {
      label: this.$t("_action.send_email"),
      description: this.$t(
        "_sentence.templates.send_test_email_from_template_desc"
      )
    });
  }

  public renderSendTestEmail(h: CreateElement) {
    return h(
      "button",
      {
        props: {
          type: "button"
        },
        attrs: {
          class: "button"
        },
        on: {
          click: (e: Event) => {
            e.preventDefault();
            e.stopPropagation();

            this.$store.dispatch(
              `data/${DataModules.TEMPLATES}/openSendTestEmailModal`,
              {
                parent: this,
                props: {
                  templateId: this.template?.id,
                  objectType: this.template?.category?.code,
                  recipientTypeId: this.template?.recipient_type_id
                }
              }
            );
          }
        }
      },
      this.$t("_sentence.templates.send_test_email").toString()
    );
  }

  public renderTemplateContent(h: CreateElement) {
    if (!this.selectedChannel) return null;

    return h(TemplateContent, {
      props: {
        body: this.form.body,
        categories: this.availableCategoriesForContent,
        category: this.selectedCategory,
        processing: this.$templateData.isProcessing,
        subject: this.form.subject,
        recipientType: this.selectedRecipientType,
        channel: this.selectedChannel,
        template: this.$templateData.template
      },
      on: {
        "update:subject": e => (this.form.subject = e),
        "update:body": e => (this.form.body = e)
      }
    });
  }

  public renderIsActive(h: CreateElement) {
    if (!this.showActiveToggle || this.$templateData.isDefault) return null;
    return h(IsActiveField, {
      props: { value: this.form.enabled },
      on: { input: e => (this.form.enabled = e) }
    });
  }

  public renderSendAttachment(h: CreateElement) {
    if (this.selectedChannel?.code !== NotificationChannelCodes.EMAIL) return;

    return h(SendAttachmentField, {
      props: { value: this.form.send_attachment },
      on: { input: e => (this.form.send_attachment = e) }
    });
  }

  public renderName(h: CreateElement) {
    return this.renderContextualise(
      h(NameField, {
        props: { value: this.form.name },
        attrs: { rules: "required" },
        on: { input: e => (this.form.name = e) }
      }),
      {
        label: `${this.$t("_.name_this_notification")}*`,
        description: this.$t("_sentence.name_this_notification_desc")
      }
    );
  }

  public renderTranslations(h: CreateElement) {
    if (this.$templateData.isNew || this.$templateData.isLoading) return null;
    return this.renderContextualise(
      h(ActionContentsTable, {
        props: {
          id: "NTCT1",
          categories: this.availableCategoriesForContent,
          template: this.$templateData.template
        },
        on: {
          "contents-updated": async () => {
            this.$templateMethods.reloadContents();
          }
        }
      }),
      {
        label: this.$t("_.translations"),
        description: this.$t("_sentence.templates.translations_desc")
      }
    );
  }

  public renderChannels(h: CreateElement) {
    if (!this.showChannelSelector) return null;

    return h(ChannelsField, {
      props: {
        value: this.form.channel_id,
        channels: this.$templateData.channels,
        isDisabled: this.isDisabled || this.preselectedChannelCode
      },
      on: {
        input: e => {
          this.form.channel_id = e;
          /**
           * Reset recipient type when channel changes
           */
          this.form.recipient_type_id = undefined;
        }
      }
    });
  }

  public renderRecipients(h: CreateElement) {
    if (!this.showRecipientSelector || !this.selectedChannel) return null;
    return h(RecipientsField, {
      props: {
        value: this.form.recipient_type_id,
        recipientTypes: this.availableRecipientTypes,
        isDisabled: this.isDisabled
      },
      on: { input: e => (this.form.recipient_type_id = e) }
    });
  }

  public renderHooks(h: CreateElement) {
    if (!this.showHookSelector || !this.selectedChannel) return null;

    return h(HooksField, {
      props: {
        value: this.form.hook_ids,
        categories: this.$templateData.categories,
        selectedCategory: this.selectedCategory,
        isDisabled:
          this.$templateData.isReadOnly || this.$templateData.isProcessing
      },
      on: {
        input: e => {
          this.form.hook_ids = e;
          if (!this.$templateData.isNew) return;
          /**
           * Set hook's category when hook is selected
           */
          this.form.category_code = this.selectedHooks?.[0]?.category?.code;
        }
      }
    });
  }

  public openSendTestEmailModal() {
    this.$store.dispatch(
      `data/${DataModules.TEMPLATES}/openSendTestEmailModal`,
      {
        parent: this,
        props: {
          templateId: this.template.id,
          objectType: this.selectedCategory?.code,
          recipientTypeId: this.form.recipient_type_id
        }
      }
    );
  }
}
